import { apiClient } from '../../../shared/api/axios-instance'
import type { Role, RoleDetail, PermissionGroup } from '../types/role.types'

export interface CreateRoleData {
    name: string
    display_name: string
    description?: string
}

export interface UpdateRoleData {
    name?: string
    display_name?: string
    description?: string
}

export interface AssignPermissionsData {
    permission_ids: number[]
}

const roleService = {
    /**
     * Get all roles with counts
     */
    async getRoles(): Promise<Role[]> {
        const response = await apiClient.get('admin/roles')
        // Backend returns { success: true, data: [...] }
        const data = response.data.data || response.data
        // Ensure we always return an array
        return Array.isArray(data) ? data : []
    },

    /**
     * Get role details with permissions
     */
    async getRole(id: number): Promise<RoleDetail> {
        const response = await apiClient.get(`admin/roles/${id}`)
        return response.data.data || response.data
    },

    /**
     * Create a new role
     */
    async createRole(data: CreateRoleData): Promise<Role> {
        const response = await apiClient.post('admin/roles', data)
        return response.data.data || response.data
    },

    /**
     * Update a role
     */
    async updateRole(id: number, data: UpdateRoleData): Promise<Role> {
        const response = await apiClient.put(`admin/roles/${id}`, data)
        return response.data.data || response.data
    },

    /**
     * Delete a role
     */
    async deleteRole(id: number): Promise<void> {
        await apiClient.delete(`admin/roles/${id}`)
    },

    /**
     * Assign permissions to a role
     */
    async assignPermissions(id: number, data: AssignPermissionsData): Promise<void> {
        await apiClient.post(`admin/roles/${id}/permissions`, data)
    },

    /**
     * Get all permissions grouped by category
     */
    async getPermissions(): Promise<PermissionGroup[]> {
        const response = await apiClient.get('admin/permissions')
        // Handle both wrapped and unwrapped responses
        const data = response.data.data || response.data
        return Array.isArray(data) ? data : []
    },
}

export default roleService
